import React from "react";
import {
    Button,
    Checkbox,
    Col,
    Collapse,
    DatePicker,
    Divider,
    Input,
    InputNumber,
    Row,
    Select,
    Tag,
    Card,
    Typography,
    Radio,
} from "antd";
import {useContext, useEffect, useState} from "react";
import {Control, FormProvider, useForm, useFormContext, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import AppContext from "../../../context/app/context";
import AuthContext from "../../../context/auth/context";
import CategoryContext from "../../../context/category/context";
import ProductContext from "../../../context/product/context";
import ProductContextProvider from "../../../context/product/provider";
import {AttributeType} from "../../../models/attribute/enums";
import {IAttributeDetails} from "../../../models/attribute/resopnse";
import {ICategory} from "../../../models/category/response";
import {BooleanStatus, getProductStatus, getProductType} from "../../../models/product/enum";
import {IUpdateProduct, IStoreQuantityRequest} from "../../../models/product/request";
import {IQuantity} from "../../../models/product/response";
import {ITrademark} from "../../../models/trademark/response";
import EndPoints from "../../../services/end-points";
import {languages} from "../../../utils/helpers/constants";
import {execute} from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import {Permissions} from "../../../utils/Permissions/Permissions";
import styles from "./style.module.scss";
import {ErrorMessage} from "@hookform/error-message";
import dayjs from "dayjs";
import {isArray} from "lodash";
import {getTranslations} from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import Image from "../../general/antd/image";
import ImageUploader from "../../form-components/image-uploader-client";
import {MediumFor} from "../../../models/medium/enum";
import RichEditor from "../../form-components/rich-text-editor";
import {ITag} from "../../../models/tag/response";
import {IUnit} from "../../../models/unit/response";
import FileUploader from "../../form-components/file-uploader";
import {IUnitPartition} from "../../../models/unit-partition/response";
import {errorNotification, successNotification} from "../../../utils/helpers/notification";
import {appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, ImageProcessingMode, resolveUploadFile} from "../../../utils/helpers/media-upload";
import {DeleteOutlined} from "@ant-design/icons";

const Option = Select.Option;

const normalizeBarcodes = (barcodes: unknown): string[] => {
    if (!Array.isArray(barcodes)) {
        return [];
    }

    const seen = new Set<string>();

    return barcodes.reduce<string[]>((result, barcode) => {
        const value = String(barcode).trim();

        if (!value || seen.has(value)) {
            return result;
        }

        seen.add(value);
        result.push(value);
        return result;
    }, []);
};

const isStoreBarcodesDirty = (dirtyStoreQuantities: unknown, index: number): boolean => {
    if (!Array.isArray(dirtyStoreQuantities)) {
        return false;
    }

    const dirtyStoreQuantity = dirtyStoreQuantities[index];

    return Boolean(
        dirtyStoreQuantity &&
        typeof dirtyStoreQuantity === "object" &&
        (dirtyStoreQuantity as Record<string, unknown>).barcodes
    );
};

// Separate component for store quantity card to properly use hooks
interface IStoreQuantityCardProps {
    sq: IQuantity;
    index: number;
    control: Control<any>;
    canManagePurchasePrice: boolean;
    t: (key: string) => string;
}

const StoreQuantityCard: React.FC<IStoreQuantityCardProps> = ({
    sq,
    index,
    control,
    canManagePurchasePrice,
    t,
}) => {
    const storeName = typeof sq.store_name === 'object' ? sq.store_name?.ar : sq.store_name;
    const watchGeneralDiscount = useWatch({ control, name: `storeQuantities.${index}.general_discount` });
    const watchSpecialDiscount = useWatch({ control, name: `storeQuantities.${index}.special_discount` });

    return (
        <Card
            key={sq.store_id}
            size="small"
            title={storeName}
            style={{ marginBottom: 16 }}
        >
            <Row gutter={[16, 8]}>
                {/* Hidden store_id */}
                <Controller
                    name={`storeQuantities.${index}.store_id`}
                    control={control}
                    defaultValue={sq.store_id}
                    render={({field}: any) => <input type="hidden" {...field} />}
                />
                <Controller
                    name={`storeQuantities.${index}.store_name`}
                    control={control}
                    defaultValue={sq.store_name}
                    render={({field}: any) => <input type="hidden" {...field} />}
                />

                <Col xs={24} sm={24} md={12} lg={12}>
                    <FormItem label={t("barcodes")}>
                        <Controller
                            name={`storeQuantities.${index}.barcodes`}
                            control={control}
                            defaultValue={normalizeBarcodes(sq.barcodes)}
                            render={({field}: any) => (
                                <Select
                                    {...field}
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    tokenSeparators={[",", "\n"]}
                                    value={normalizeBarcodes(field.value)}
                                    onChange={(value: string[]) => field.onChange(normalizeBarcodes(value))}
                                />
                            )}
                        />
                    </FormItem>
                </Col>

                <Col xs={12} sm={12} md={6} lg={6}>
                    <FormItem label={t("price")}>
                        <Controller
                            name={`storeQuantities.${index}.price`}
                            control={control}
                            render={({field}: any) => (
                                <InputNumber
                                    {...field}
                                    style={{ width: '100%' }}
                                    min={0}
                                />
                            )}
                        />
                    </FormItem>
                </Col>

                <Col xs={12} sm={12} md={6} lg={6}>
                    <FormItem label={t("price_in_dollar")}>
                        <Controller
                            name={`storeQuantities.${index}.price_in_dollar`}
                            control={control}
                            render={({field}: any) => (
                                <InputNumber
                                    {...field}
                                    style={{ width: '100%' }}
                                    min={0}
                                    prefix="$"
                                />
                            )}
                        />
                    </FormItem>
                </Col>

                {canManagePurchasePrice && (
                    <>
                        <Col xs={12} sm={12} md={6} lg={6}>
                            <FormItem label={t("purchase_price")}>
                                <Controller
                                    name={`storeQuantities.${index}.purchase_price`}
                                    control={control}
                                    render={({field}: any) => (
                                        <InputNumber
                                            {...field}
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={99999999}
                                        />
                                    )}
                                />
                            </FormItem>
                        </Col>

                        <Col xs={12} sm={12} md={6} lg={6}>
                            <FormItem label={t("purchase_price_in_dollar")}>
                                <Controller
                                    name={`storeQuantities.${index}.purchase_price_in_dollar`}
                                    control={control}
                                    render={({field}: any) => (
                                        <InputNumber
                                            {...field}
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={99999999}
                                            prefix="$"
                                        />
                                    )}
                                />
                            </FormItem>
                        </Col>
                    </>
                )}

                <Col xs={12} sm={6} md={3} lg={3}>
                    <Controller
                        name={`storeQuantities.${index}.general_discount`}
                        control={control}
                        render={({field}: any) => (
                            <Checkbox
                                {...field}
                                checked={field.value}
                                style={{ marginTop: 30 }}
                            >
                                {t("general_discount")}
                            </Checkbox>
                        )}
                    />
                </Col>

                {watchGeneralDiscount && (
                    <Col xs={12} sm={12} md={6} lg={6}>
                        <FormItem label={`${t("value_general_discount")}(%)`}>
                            <Controller
                                name={`storeQuantities.${index}.value_general_discount`}
                                control={control}
                                render={({field}: any) => (
                                    <InputNumber
                                        {...field}
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        suffix="%"
                                    />
                                )}
                            />
                        </FormItem>
                    </Col>
                )}

                <Col xs={12} sm={6} md={3} lg={3}>
                    <Controller
                        name={`storeQuantities.${index}.special_discount`}
                        control={control}
                        render={({field}: any) => (
                            <Checkbox
                                {...field}
                                checked={field.value}
                                style={{ marginTop: 30 }}
                            >
                                {t("special_discount")}
                            </Checkbox>
                        )}
                    />
                </Col>

                {watchSpecialDiscount && (
                    <Col xs={12} sm={12} md={6} lg={6}>
                        <FormItem label={`${t("value_special_discount")}(%)`}>
                            <Controller
                                name={`storeQuantities.${index}.value_special_discount`}
                                control={control}
                                render={({field}: any) => (
                                    <InputNumber
                                        {...field}
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        suffix="%"
                                    />
                                )}
                            />
                        </FormItem>
                    </Col>
                )}

                <Col xs={12} sm={12} md={6} lg={6}>
                    <FormItem label={t("store_status")}>
                        <Controller
                            name={`storeQuantities.${index}.status`}
                            control={control}
                            render={({field}: any) => (
                                <Select
                                    {...field}
                                    style={{ width: '100%' }}
                                    options={[BooleanStatus.ACTIVE, BooleanStatus.INACTIVE].map((s) => ({
                                        label: t(getProductStatus(s)),
                                        value: s,
                                    }))}
                                />
                            )}
                        />
                    </FormItem>
                </Col>
            </Row>
        </Card>
    );
};

const Form = () => {
    const {details, actions, loading} = useContext(ProductContext);
    const {direction} = useContext(AppContext);
    const { userPermissions: permissions } = useContext(AuthContext);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {id} = useParams();

    let userPermissions =
        permissions?.length > 0
            ? permissions
            : localStorage?.getItem("permisionStoroge")?.split(",");
    if (!userPermissions) userPermissions = [];
    const canManagePurchasePrice = userPermissions.includes(Permissions.CAN_MANAGE_PURCHASE_PRICE);

    const {allCategories, actions: {getAllData}} = useContext(CategoryContext);
    const [subCategories, setsubCategories] = useState<ICategory[]>([]);
    const [tags, setTags] = useState<ITag[]>([]);
    const [tagsLoading, setTagsLoading] = useState(true);

    const [units, setUnits] = useState<IUnit[]>([]);
    const [unitsLoading, setUnitsLoading] = useState(true);

    const [unitPartitions, setUnitPartitions] = useState<IUnitPartition[]>([]);
    const [unitPartitionsLoading, setUnitPartitionsLoading] = useState(true);

    const [imageChanged, setImageChanged] = useState(false);
    const [imageLoading, setImageLoading] = useState<boolean>(false);


    const methods = useForm<any>({
        defaultValues: {
            mainCategoryId: details?.mainCategory?.id,
            childCategoryId: details?.childCategory?.id,
            parentCategoryId: details?.parentCategory?.id,
            soldByUnit: details?.soldByUnit ?? false,
            needRecipe: details?.needRecipe ?? false,
            imageId: details?.image?.id ?? undefined,
            attributes: details?.attributes,
            description: getTranslations(details?.translations?.description) ?? null,
            extraName: getTranslations(details?.translations?.extraName),
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            status: details?.status,
            trademarkId: details?.trademark?.id,
            type: details?.type,
            tagIds: details?.tags?.map((tag) => tag?.id),
            isNew: details?.isNew ?? false,
            unitId: details?.unit?.id,
            generalDiscount: details?.generalDiscount,
            specialDiscount: details?.specialDiscount,
            freeDelivery: details?.freeDelivery,
            valueGeneralDiscount: details?.valueGeneralDiscount ?? undefined,
            valueSpecialDiscount: details?.valueSpecialDiscount ?? undefined,
            price: details?.price ?? undefined,
            priceInDollar: details?.priceInDollar ?? undefined,
            purchasePrice: undefined,
            purchasePriceInDollar: undefined,
            unitPartitionIds: details?.unitPartition?.map((unitPartition) => unitPartition?.id) ?? undefined,
            sliceId: details?.slice?.id ?? 4,
            storeId: details?.store?.id ?? undefined,
            without_background: details?.image?.without_background ?? false
        } as IUpdateProduct,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        trigger,
        formState: {errors, dirtyFields},
    } = methods;

    const [trademark, setTrademark] = useState<ITrademark[]>([]);
    const [trademarkLoading, setTrademarkLoading] = useState<boolean>(false);
    const [attributes, setAttributes] = useState<IAttributeDetails[]>(
        details?.attributes ?? null
    );
    const [keyword, setKeyword] = useState<string>();
    const [tagKeyword, setTagKeyword] = useState<string>();
    const [newImage, setNewImage] = useState<string | null>(null);
    const [newImageFile, setNewImageFile] = useState<File | undefined>();
    const [originalImageFile, setOriginalImageFile] = useState<File | undefined>();
    const [preserveOriginal, setPreserveOriginal] = useState(false);
    const [currentImageHidden, setCurrentImageHidden] = useState(false);

    const pointMapChanges = useWatch({
        control,
        name: "soldByUnit",
    });

    useEffect(() => {
        const getTrademarks = async () => {
            setTrademarkLoading(true);

            try {
                const {data} = await EndPoints.trademark.getAllTrademark({
                    keyword: keyword ? `${keyword}` : undefined,
                    light: true,
                    perPage: -1,
                });
                setTrademark(data.data);
            } catch (error) {
            } finally {
                setTrademarkLoading(false);
            }
        };
        getTrademarks();
    }, [direction, keyword]);

    useEffect(() => {
        const getTags = async () => {
            try {
                const {data} = await EndPoints.tag.getAllTags({
                    light: true,
                    perPage: -1,
                });
                setTags(data.data);
            } catch (error) {
            } finally {
                setTagsLoading(false);
            }
        };
        getTags();
        !allCategories && getAllData();
    }, [direction]);

    useEffect(() => {
        const getUnits = async () => {
            try {
                const {data} = await EndPoints.unit.getAllUnits({
                    light: true,
                    perPage: -1,
                });
                setUnits(data?.data);
            } catch (error) {
            } finally {
                setUnitsLoading(false);
            }
        };
        getUnits();
    }, [direction]);


    const selectedUnitId = useWatch({
        control,
        name: "unitId",
    });

    const currentImageId = useWatch({
        control,
        name: "imageId",
    });

    useEffect(() => {
        if (newImageFile && details?.image?.id !== currentImageId) {
            setImageChanged(true);
        } else {
            setImageChanged(false);
        }
    }, [currentImageId, details?.image?.id, newImageFile]);

    useEffect(() => {
        setCurrentImageHidden(false);
    }, [details?.image?.id]);

    useEffect(() => {
        if (selectedUnitId) {
            setUnitPartitionsLoading(true);
            EndPoints.unitPartition
                .getAllUnitPartitions({
                    light: true,
                    perPage: -1,
                    filters: [{
                        name: "unit_id",
                        operation: "eq",
                        value: selectedUnitId
                    }]
                })
                .then(({data}) => {
                    setUnitPartitions(data?.data);
                })
                .finally(() => {
                    setUnitPartitionsLoading(false);
                });
        } else {
            setUnitPartitions([]);
        }
    }, [selectedUnitId]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    const mainCategoryId = useWatch({
        control,
        name: "mainCategoryId",
    });

    const parentCategoryId = useWatch({
        control,
        name: "parentCategoryId",
    });

    const childCategoryId = useWatch({
        control,
        name: "childCategoryId",
    });

    useEffect(() => {
        parentCategoryId &&
        setAttributes(
            allCategories?.filter((cat) => cat.id === parentCategoryId)[0]
                ?.attributes
        );
    }, [parentCategoryId]);

    useEffect(() => {
        mainCategoryId &&
        setsubCategories(
            allCategories?.filter(
                (cat) => cat?.grand?.id === mainCategoryId && cat?.parent === null
            )
        );
    }, [mainCategoryId]);

    const [childCategoriess, setChildCategoriess] = useState<ICategory[]>([]);
    useEffect(() => {
        parentCategoryId &&
        setChildCategoriess(
            allCategories?.filter(
                (cat) =>
                    cat?.grand?.id === mainCategoryId &&
                    cat?.parent?.id === parentCategoryId
            )
        );
    }, [parentCategoryId]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        if (hasPendingImageUpload(imageChanged, newImage)) {
            errorNotification(t("pending_image_upload_message"));
            return;
        }

        data.idAccounting = data.idAccounting;

        await execute({
            callback: async () => {
                setLoadingSubmit(true);

                if (details) {
                    // EDIT mode (PUT) - Use storeQuantities, remove global price fields
                    const updateData = { ...data };

                    // Format storeQuantities for PUT request
                    if (updateData.storeQuantities) {
                        const dirtyStoreQuantities = dirtyFields?.storeQuantities;

                        updateData.storeQuantities = updateData.storeQuantities.map((sq: any, index: number) => {
                            const storeQuantity: IStoreQuantityRequest = {
                                store_id: sq.store_id,
                                price: sq.price,
                                price_in_dollar: sq.price_in_dollar,
                                purchase_price: sq.purchase_price,
                                purchase_price_in_dollar: sq.purchase_price_in_dollar,
                                general_discount: sq.general_discount ?? false,
                                special_discount: sq.special_discount ?? false,
                                value_general_discount: sq.general_discount ? (sq.value_general_discount || 0) : 0,
                                value_special_discount: sq.special_discount ? (sq.value_special_discount || 0) : 0,
                                status: sq.status,
                            };

                            if (isStoreBarcodesDirty(dirtyStoreQuantities, index)) {
                                storeQuantity.barcodes = normalizeBarcodes(sq.barcodes);
                            }

                            return storeQuantity;
                        });
                    }

                    // Remove global price fields - they're not allowed in PUT
                    delete updateData.price;
                    delete updateData.priceInDollar;
                    delete updateData.purchasePrice;
                    delete updateData.purchasePriceInDollar;
                    delete updateData.generalDiscount;
                    delete updateData.specialDiscount;
                    delete updateData.valueGeneralDiscount;
                    delete updateData.valueSpecialDiscount;
                    delete updateData.barcode;

                    await EndPoints.product.updateProduct(details?.id, updateData);
                } else {
                    delete data.barcode;
                    // CREATE mode (POST) - Use global price fields
                    data.valueSpecialDiscount == ""
                        ? (data.valueSpecialDiscount = 0)
                        : (data.valueSpecialDiscount = data.valueSpecialDiscount > 0 ? Math.trunc(data.valueSpecialDiscount) : undefined);
                    data.valueGeneralDiscount == ""
                        ? (data.valueGeneralDiscount = 0)
                        : (data.valueGeneralDiscount = data.valueGeneralDiscount > 0 ? Math.trunc(data.valueGeneralDiscount) : undefined);
                    data.purchasePrice === ""
                        ? (data.purchasePrice = undefined)
                        : data.purchasePrice;
                    data.purchasePriceInDollar === ""
                        ? (data.purchasePriceInDollar = undefined)
                        : data.purchasePriceInDollar;

                    await EndPoints.product.createProduct(data);
                }
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    const discount = useWatch({
        control,
        name: ["generalDiscount", "specialDiscount"],
    });
    useEffect(() => {
        if (!discount[0]) {
            setValue('valueGeneralDiscount', "");
        }

        if (!discount[1]) {
            setValue('valueSpecialDiscount', "");
        }
    }, [discount, setValue]);

    useEffect(() => {
        reset({
            without_background: details?.image?.without_background ?? false,
            mainCategoryId: details?.mainCategory?.id,
            childCategoryId: details?.childCategory?.id,
            parentCategoryId: details?.parentCategory?.id,
            extraName: getTranslations(details?.translations?.extraName),
            description: getTranslations(details?.translations?.description) ?? null,
            note: getTranslations(details?.translations?.note) ?? null,
            name: getTranslations(details?.translations?.name),
            soldByUnit: details?.soldByUnit,
            status: details?.status,
            freeDelivery: details?.freeDelivery,
            type: details?.type,
            trademarkId: details?.trademark?.id,
            attributes: details?.attributes?.sort((a) => a.order)?.sort((a) => a.id),
            needRecipe: details?.needRecipe,
            imageId: details?.image?.id ?? undefined,
            tagIds: details?.tags?.map((tag) => tag?.id),
            unitPartitionIds: details?.unitPartition?.map((unitPartition) => unitPartition?.id) ?? undefined,
            isNew: details?.isNew,
            unitId: details?.unit?.id,
            sliceId: details?.slice?.id ?? undefined,
            storeId: details?.store?.id ?? undefined,
            // For EDIT mode: populate storeQuantities from details
            storeQuantities: details?.storeQuantities?.map((sq: IQuantity) => ({
                store_id: sq.store_id,
                store_name: sq.store_name,
                price: sq.price,
                price_in_dollar: sq.price_in_dollar,
                purchase_price: sq.purchase_price,
                purchase_price_in_dollar: sq.purchase_price_in_dollar,
                general_discount: sq.general_discount,
                special_discount: sq.special_discount,
                value_general_discount: sq.value_general_discount,
                value_special_discount: sq.value_special_discount,
                status: sq.status,
                barcodes: normalizeBarcodes(sq.barcodes),
            })) ?? [],
            // Keep these for CREATE mode (they won't be used in PUT)
            generalDiscount: details?.generalDiscount,
            specialDiscount: details?.specialDiscount,
            valueGeneralDiscount: details?.valueGeneralDiscount ?? undefined,
            valueSpecialDiscount: details?.valueSpecialDiscount ?? undefined,
            price: details?.price ?? undefined,
            priceInDollar: details?.priceInDollar ?? undefined,
            purchasePrice: undefined,
            purchasePriceInDollar: undefined,
        } as IUpdateProduct);
    }, [details]);


    const handleUploadNewImage = async () => {
        if (!newImage) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        setImageLoading(true);

        try {
            const formData = new FormData();
            const timestamp = new Date().getTime();
            const file = await resolveUploadFile({
                imageUrl: newImage,
                processedFile: newImageFile,
                originalFile: originalImageFile,
                preserveOriginal,
                fallbackName: `product-image-${timestamp}`,
            });

            formData.append("medium", file);
            formData.append("type", "1");

            formData.append("for", MediumFor.UserProfile);

            const withoutBackgroundValue = methods.getValues("without_background");
            appendImageProcessingFields(
                formData,
                getImageProcessingMode(Boolean(withoutBackgroundValue), preserveOriginal),
            );

            const uploadResponse = await EndPoints.media.uploadMedia(formData);

            if (!uploadResponse?.id) {
                throw new Error("Invalid response from server - no image ID returned");
            }
            const uploadedImageId = uploadResponse.id;

            setValue("imageId", uploadedImageId, {shouldDirty: true});
            setNewImage(uploadResponse?.url ?? newImage);
            setNewImageFile(undefined);
            setOriginalImageFile(undefined);
            setPreserveOriginal(false);
            setImageChanged(false);

            successNotification(t("image_uploaded_successfully"));

        } catch (error) {
            let errorMessage = t("failed_to_upload_image");

            if (error.message.includes("Failed to fetch")) {
                errorMessage = t("failed_to_load_image");
            } else if (error.message.includes("Invalid response")) {
                errorMessage = t("server_error_try_again");
            }

            errorNotification(errorMessage);
        } finally {
            setImageLoading(false)
        }
    };

    const withoutBackground = useWatch({control, name: "without_background"});
    const imageProcessingMode = getImageProcessingMode(Boolean(withoutBackground), preserveOriginal);
    const currentImageVisible = details?.image && !currentImageHidden;
    const imageDeleteButtonStyle: React.CSSProperties = {
        position: "absolute",
        top: 8,
        zIndex: 2,
        background: "rgba(255,255,255,.92)",
        boxShadow: "0 2px 8px rgba(0,0,0,.12)",
        ...(direction === "rtl" ? {left: 8} : {right: 8}),
    };

    const handleRemoveNewImage = async () => {
        const shouldRestoreCurrentImage = Boolean(details?.image && !currentImageHidden);

        setValue("imageId", shouldRestoreCurrentImage ? details?.image?.id : undefined, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        setValue("without_background", shouldRestoreCurrentImage ? details?.image?.without_background ?? false : false);
        await trigger("imageId");
        setNewImage(null);
        setNewImageFile(undefined);
        setOriginalImageFile(undefined);
        setPreserveOriginal(false);
        setImageChanged(false);
    };

    const handleDeleteCurrentImage = async () => {
        setValue("imageId", null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        await trigger("imageId");
        setCurrentImageHidden(true);
        setNewImage(null);
        setNewImageFile(undefined);
        setOriginalImageFile(undefined);
        setPreserveOriginal(false);
        setImageChanged(false);
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
          <span className={styles.title}>
            {details ? t("update_product") : t("add_product")}
          </span>
                    <Divider className={styles.divider}/>
                    {loading.includes("details") ? (
                        <div style={{height: "60vh"}}>
                            <Spin/>
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`name[${lang?.code}]`}
                                                label={`${t("name")} (${lang?.name})`}
                                                input={{type: "text"}}
                                                rules={{required: true}}
                                            />
                                        </Col>
                                    );
                                })}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`extraName[${lang?.code}]`}
                                                label={`${t("extra_name")} (${lang?.name})`}
                                                input={{type: "text"}}
                                                rules={{required: false}}
                                            />
                                        </Col>
                                    );
                                })}

                                {/* Global price fields - only for CREATE mode */}
                                {!id && (
                                    <>
                                        <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                            <FieldBuilder
                                                name="price"
                                                label={`${t("price")}`}
                                                width="large"
                                                rules={{required: true}}
                                                input={{
                                                    type: "number",
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                            <FieldBuilder
                                                name="priceInDollar"
                                                label={`${t("price_in_dollar")}`}
                                                width="large"
                                                rules={{required: true}}
                                                input={{
                                                    type: "number",
                                                }}
                                                suffix={"$"}
                                            />
                                        </Col>
                                        {canManagePurchasePrice && (
                                            <>
                                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                                    <FieldBuilder
                                                        name="purchasePrice"
                                                        label={`${t("purchase_price")}`}
                                                        width="large"
                                                        rules={{min: 0, max: 99999999}}
                                                        input={{
                                                            type: "number",
                                                        }}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                                    <FieldBuilder
                                                        name="purchasePriceInDollar"
                                                        label={`${t("purchase_price_in_dollar")}`}
                                                        width="large"
                                                        rules={{min: 0, max: 99999999}}
                                                        input={{
                                                            type: "number",
                                                        }}
                                                        suffix={"$"}
                                                    />
                                                </Col>
                                            </>
                                        )}
                                    </>
                                )}
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FieldBuilder
                                        name="type"
                                        label={`${t("type")}`}
                                        width="large"
                                        rules={{required: true}}
                                        input={{
                                            type: "select",
                                            loading: false,
                                            allowSearch: true,
                                            options: [1, 2]?.map((state, index) => {
                                                return {
                                                    key: index,
                                                    label: t(getProductType(state)),
                                                    value: state,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FieldBuilder
                                        name="status"
                                        label={`${t("status")}`}
                                        width="large"
                                        rules={{required: true}}
                                        input={{
                                            type: "select",
                                            loading: false,
                                            options: [1, 2]?.map((state, index) => {
                                                return {
                                                    key: index,
                                                    label: t(getProductStatus(state)),
                                                    value: state,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                {/* <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <FieldBuilder
                    name="sliceId"
                    label={`${t("slice")}`}
                    width="large"
                    input={{
                      type: "select",
                      allowSearch: true,
                      loading: sliceLoading,
                      options: slice.map((slice, index) => {
                        return {
                          label: slice?.name,
                          value: slice.id as number,
                        };
                      }),
                    }}
                  />
                </Col> */}
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FieldBuilder
                                        name="trademarkId"
                                        label={`${t("trademark")}`}
                                        rules={{required: true}}
                                        width="large"
                                        input={{
                                            type: "select",
                                            allowSearch: true,
                                            loading: trademarkLoading,
                                            options: trademark.map((trademark, index) => {
                                                return {
                                                    label: trademark?.name,
                                                    value: trademark.id as number,
                                                };
                                            }),
                                        }}
                                    />

                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FormItem label={t("tags")}>
                                        <Controller
                                            name="tagIds"
                                            control={control}
                                            rules={{required: false}}
                                            render={({field: {...field}}) => {
                                                return (
                                                    <Select
                                                        loading={tagsLoading}
                                                        {...field}
                                                        onSearch={(val) => {
                                                            setTagKeyword(val);
                                                        }}
                                                        style={{width: "100%"}}
                                                        allowClear
                                                        mode="multiple"
                                                        placeholder={t("tags")}
                                                        filterOption={false}
                                                    >
                                                        {tags
                                                            .filter((tr) =>
                                                                tagKeyword
                                                                    ? tr?.name?.includes(tagKeyword)
                                                                    : true
                                                            )
                                                            .map((tr, index) => {
                                                                return (
                                                                    <Option
                                                                        key={index}
                                                                        value={tr?.id}
                                                                        label={
                                                                            <Tag color={tr?.color}>{tr.name}</Tag>
                                                                        }
                                                                    >
                                                                        <Tag color={tr?.color}>{tr?.name}</Tag>
                                                                    </Option>
                                                                );
                                                            })}
                                                    </Select>
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FormItem label={t("main_category")}>
                                        <Controller
                                            name="mainCategoryId"
                                            control={control}
                                            rules={{required: true}}
                                            render={({field: {...field}}) => {
                                                return (
                                                    <Select
                                                        loading={false}
                                                        optionLabelProp="label"
                                                        optionFilterProp="label"
                                                        style={{width: "100%"}}
                                                        allowClear
                                                        showSearch
                                                        onSelect={() => {
                                                            setValue("parentCategoryId", null);
                                                            setValue("childCategoryId", null);
                                                        }}
                                                        placeholder={t("categories")}
                                                        {...field}
                                                        options={allCategories
                                                            ?.filter(
                                                                (cat) =>
                                                                    cat?.parent === null && cat?.grand === null
                                                            )
                                                            ?.map((tr, index) => ({
                                                                label: tr.name,
                                                                value: tr.id,
                                                            }))}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>

                                    <FormItem label={t("parent_category")}>
                                        <Controller
                                            name="parentCategoryId"
                                            control={control}
                                            rules={{required: true}}
                                            render={({field: {...field}}) => {
                                                return (
                                                    <Select
                                                        showSearch
                                                        loading={false}
                                                        {...field}
                                                        style={{width: "100%"}}
                                                        allowClear
                                                        placeholder={t("categories")}
                                                        optionLabelProp="label"
                                                        optionFilterProp="label"
                                                        options={subCategories?.map((tr, index) => ({
                                                            label: tr.name,
                                                            value: tr.id,
                                                        }))}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    {/* <FieldBuilder
                      name="childCategoryId"
                      label={`${t("childern_category")}`}
                      width="large"
                      rules={{ required: false }}
                      // disabled={childCategoryId == null}
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: false,
                        options: childCategoriess?.map((t) => {
                          return {
                            value: t.id,
                            label: t.name,
                          };
                        }) ?? []
                      }}
                    /> */}
                                    <FormItem label={t("childern_category")}>
                                        <Controller
                                            name="childCategoryId"
                                            control={control}
                                            rules={{required: true}}
                                            render={({field: {...field}}) => {
                                                return (
                                                    <Select
                                                        loading={false}
                                                        {...field}
                                                        style={{width: "100%"}}
                                                        allowClear
                                                        showSearch
                                                        placeholder={t("categories")}
                                                        optionLabelProp="label"
                                                        optionFilterProp="label"
                                                        options={childCategoriess?.map((tr, index) => ({
                                                            label: tr.name,
                                                            value: tr.id,
                                                        }))}

                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                    <FieldBuilder
                                        name="unitId"
                                        label={`${t("unit")}`}
                                        width="large"
                                        rules={{required: true}}
                                        input={{
                                            type: "select",
                                            allowSearch: true,
                                            loading: unitsLoading,
                                            options: units?.map((unit, index) => {
                                                return {
                                                    key: index,
                                                    label: unit.name,
                                                    value: unit.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                {pointMapChanges && selectedUnitId && (
                                    <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                        <FormItem label={t("unit_partitions")}>
                                            <Controller
                                                name="unitPartitionIds"
                                                control={control}
                                                render={({field}) => (
                                                    <Select
                                                        style={{width: "100%"}}
                                                        mode="multiple"
                                                        allowClear
                                                        loading={unitPartitionsLoading}
                                                        placeholder={t("select_unit_partitions")}
                                                        options={unitPartitions.map((up) => ({
                                                            value: up.id,
                                                            label: up.name,
                                                        }))}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        </FormItem>
                                    </Col>
                                )}
                                <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                    <FieldBuilder
                                        label={`${t("sold_by_unit")}`}
                                        name="soldByUnit"
                                        input={{type: "checkBox"}}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                    <FieldBuilder
                                        label={`${t("need_recipe")}`}
                                        name="needRecipe"
                                        input={{type: "checkBox"}}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                    <FieldBuilder
                                        label={`${t("free_delivery")}`}
                                        name="freeDelivery"
                                        input={{type: "checkBox"}}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                    <FieldBuilder
                                        label={`${t("is_new")}`}
                                        name="isNew"
                                        input={{type: "checkBox"}}
                                    />
                                </Col>
                                {/* Global discount fields - only for CREATE mode */}
                                {!id && (
                                    <>
                                        <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                            <Controller
                                                name="generalDiscount"
                                                control={control}
                                                rules={{required: false}}
                                                render={({field}) => {
                                                    return (
                                                        <>
                                                            <Checkbox
                                                                {...field}
                                                                checked={field.value}
                                                                className="shadow"
                                                            >
                                                                {t("general_discount")}
                                                            </Checkbox>
                                                        </>
                                                    );
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                            <Controller
                                                name="specialDiscount"
                                                control={control}
                                                rules={{required: false}}
                                                render={({field}) => {
                                                    return (
                                                        <>
                                                            <Checkbox
                                                                {...field}
                                                                checked={field.value}
                                                                className="shadow"
                                                            >
                                                                {t("special_discount")}
                                                            </Checkbox>
                                                        </>
                                                    );
                                                }}
                                            />
                                        </Col>
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                            <FieldBuilder
                                                label={`${t("value_general_discount")}(%)`}
                                                name="valueGeneralDiscount"
                                                input={{type: "number"}}
                                                disabled={discount[0] ? false : true}
                                            />
                                        </Col>
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                            <FieldBuilder
                                                label={`${t("value_special_discount")}(%)`}
                                                name="valueSpecialDiscount"
                                                input={{type: "number"}}
                                                disabled={discount[1] ? false : true}
                                            />
                                        </Col>
                                    </>
                                )}

                                {/* Store Quantities Section - only for EDIT mode */}
                                {id && details?.storeQuantities && details.storeQuantities.length > 0 && (
                                    <Col span={24}>
                                        <Collapse style={{ marginTop: 16 }}>
                                            <Collapse.Panel header={t("store_prices")} key="store-prices">
                                                {details.storeQuantities.map((sq: IQuantity, index: number) => (
                                                    <StoreQuantityCard
                                                        key={sq.store_id}
                                                        sq={sq}
                                                        index={index}
                                                        control={control}
                                                        canManagePurchasePrice={canManagePurchasePrice}
                                                        t={t}
                                                    />
                                                ))}
                                            </Collapse.Panel>
                                        </Collapse>
                                    </Col>
                                )}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`note[${lang?.code}]`}
                                                label={`${t("note")} (${lang?.name})`}
                                                input={{type: "text-area"}}
                                                rules={{required: false}}
                                            />
                                        </Col>
                                    );
                                })}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={24}>
                                            <div>
                                                <FormItem label={`${t("description")} (${lang?.name})`}>
                                                    <Controller
                                                        name={`description[${lang?.code}]`}
                                                        rules={{
                                                            required: {
                                                                value: false,
                                                                message: `${t("field_is_required_message")}`,
                                                            },
                                                        }}
                                                        render={({field: {ref, ...field}}) => {
                                                            return (
                                                                <RichEditor
                                                                    value={field?.value ?? ""}
                                                                    onChange={field?.onChange}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </FormItem>
                                            </div>
                                        </Col>
                                    );
                                })}
                                {attributes
                                    ?.sort((a) => a.order)
                                    ?.sort((a) => a.id)
                                    ?.map((attribute, index) => {
                                        return (
                                            <>
                                                <Controller
                                                    name={`attributes[${index}].attributeId`}
                                                    control={control}
                                                    defaultValue={attribute?.id}
                                                    render={({field: {...field}}) => {
                                                        return <Input {...field} type="hidden"/>;
                                                    }}
                                                />
                                                <Col span={8}>
                                                    <FormItem label={attribute?.name}>
                                                        <Controller
                                                            name={`attributes[${index}].values[]`}
                                                            control={control}
                                                            rules={{
                                                                required: {
                                                                    value:
                                                                        attribute?.type === AttributeType.CHECKBOX
                                                                            ? false
                                                                            : attribute?.isRequired,
                                                                    message: `${t("field_is_required_message")}`,
                                                                },
                                                            }}
                                                            render={({field}) => {
                                                                switch (attribute?.type) {
                                                                    case AttributeType.NUMBER:
                                                                        return (
                                                                            <Input
                                                                                type="number"
                                                                                onBlur={field.onBlur}
                                                                                defaultValue={field.value}
                                                                                onChange={(e) => {
                                                                                    field.onChange(
                                                                                        e?.target?.value
                                                                                            ? [e?.target?.value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                            />
                                                                        );
                                                                    case AttributeType.COLOR:
                                                                        return (
                                                                            <Select
                                                                                style={{width: "100%"}}
                                                                                placeholder={t("please_select")}
                                                                                onChange={(value) => {
                                                                                    field.onChange(
                                                                                        value
                                                                                            ? isArray(value)
                                                                                                ? value
                                                                                                : [value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                                value={
                                                                                    isArray(field.value)
                                                                                        ? !attribute?.isMultiple
                                                                                            ? field.value[0]
                                                                                            : field.value
                                                                                        : field.value
                                                                                }
                                                                                onBlur={field.onBlur}
                                                                                allowClear
                                                                                optionLabelProp="label"
                                                                                showSearch
                                                                                optionFilterProp="label"
                                                                                mode={
                                                                                    attribute?.isMultiple
                                                                                        ? "multiple"
                                                                                        : null
                                                                                }
                                                                            >
                                                                                {attribute?.attributeParams?.map(
                                                                                    (param, index) => {
                                                                                        return (
                                                                                            <Option
                                                                                                key={index}
                                                                                                value={`${param?.id}`}
                                                                                                label={
                                                                                                    <Tag
                                                                                                        color={param?.data?.hex}>
                                                                                                        {param?.data?.hex} assa
                                                                                                    </Tag>
                                                                                                }
                                                                                            >
                                                                                                <Tag
                                                                                                    color={param?.data?.hex}>
                                                                                                    {param?.data?.hex}assa
                                                                                                </Tag>
                                                                                            </Option>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </Select>
                                                                        );
                                                                    case AttributeType.TEXT_DROP_DOWN:
                                                                        return (
                                                                            <Select
                                                                                style={{width: "100%"}}
                                                                                placeholder={t("please_select")}
                                                                                onChange={(value) => {
                                                                                    field.onChange(
                                                                                        value
                                                                                            ? isArray(value)
                                                                                                ? value
                                                                                                : [value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                                value={
                                                                                    isArray(field.value)
                                                                                        ? !attribute?.isMultiple
                                                                                            ? field.value[0]
                                                                                            : field.value
                                                                                        : field.value
                                                                                }
                                                                                onBlur={field.onBlur}
                                                                                allowClear
                                                                                optionLabelProp="label"
                                                                                showSearch
                                                                                optionFilterProp="label"
                                                                                mode={
                                                                                    attribute?.isMultiple
                                                                                        ? "multiple"
                                                                                        : null
                                                                                }
                                                                                options={attribute?.attributeParams?.map(
                                                                                    (param, index) => {
                                                                                        return {
                                                                                            value: `${param?.id}`,
                                                                                            label: param?.name as string,
                                                                                        };
                                                                                    }
                                                                                )}
                                                                            />
                                                                        );
                                                                    case AttributeType.IMAGE_DROP_DOWN:
                                                                        return (
                                                                            <Select
                                                                                style={{width: "100%"}}
                                                                                placeholder={t("please_select")}
                                                                                onChange={(value) => {
                                                                                    field.onChange(
                                                                                        value
                                                                                            ? isArray(value)
                                                                                                ? value
                                                                                                : [value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                                value={
                                                                                    isArray(field.value)
                                                                                        ? !attribute?.isMultiple
                                                                                            ? field.value[0]
                                                                                            : field.value
                                                                                        : field.value
                                                                                }
                                                                                onBlur={field.onBlur}
                                                                                allowClear
                                                                                optionLabelProp="label"
                                                                                showSearch
                                                                                optionFilterProp="label"
                                                                                mode={
                                                                                    attribute?.isMultiple
                                                                                        ? "multiple"
                                                                                        : null
                                                                                }
                                                                            >
                                                                                {attribute?.attributeParams?.map(
                                                                                    (param, index) => {
                                                                                        return (
                                                                                            <Option
                                                                                                key={index}
                                                                                                value={`${param?.id}`}
                                                                                                label={
                                                                                                    <Image
                                                                                                        src={
                                                                                                            param?.data?.image?.url
                                                                                                        }
                                                                                                        className={
                                                                                                            styles.optionImage
                                                                                                        }
                                                                                                        preview={false}
                                                                                                    />
                                                                                                    // param?.name
                                                                                                }
                                                                                            >
                                                                                                <div>
                                                  <span>
                                                    <Image
                                                        src={
                                                            param?.data?.image?.url
                                                        }
                                                        className={
                                                            styles.optionImage
                                                        }
                                                        preview={false}
                                                    />
                                                  </span>
                                                                                                    <span
                                                                                                        className={styles.mx1}>
                                                    {param?.name}
                                                  </span>
                                                                                                </div>
                                                                                            </Option>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </Select>
                                                                        );
                                                                    case AttributeType.CHECKBOX:
                                                                        return (
                                                                            <Checkbox
                                                                                onChange={(e) => {
                                                                                    field.onChange([
                                                                                        `${e.target.checked}`,
                                                                                    ]);
                                                                                }}
                                                                                defaultChecked={
                                                                                    field.value
                                                                                        ? isArray(field.value)
                                                                                            ? field.value[0] === "true"
                                                                                            : field.value === "true"
                                                                                        : false
                                                                                }
                                                                            >
                                                                                {attribute?.name}
                                                                            </Checkbox>
                                                                        );
                                                                    case AttributeType.DATE:
                                                                        return (
                                                                            <DatePicker
                                                                                style={{width: "100%"}}
                                                                                format=""
                                                                                onBlur={field.onBlur}
                                                                                onChange={(value) => {
                                                                                    field.onChange(
                                                                                        value ? [value] : null
                                                                                    );
                                                                                }}
                                                                                defaultValue={
                                                                                    field.value
                                                                                        ? isArray(field.value)
                                                                                            ? dayjs(field.value[0]) // Use dayjs() instead of moment()
                                                                                            : dayjs(field.value) // Use dayjs() instead of moment()
                                                                                        : undefined // Use dayjs() instead of moment()
                                                                                }
                                                                            />
                                                                        );
                                                                    case AttributeType.NUMBER:
                                                                        return (
                                                                            <Input
                                                                                type="number"
                                                                                onBlur={field.onBlur}
                                                                                defaultValue={field.value}
                                                                                onChange={(e) => {
                                                                                    field.onChange(
                                                                                        e?.target?.value
                                                                                            ? [e?.target?.value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                            />
                                                                        );

                                                                    default:
                                                                        return (
                                                                            <Input
                                                                                onBlur={field.onBlur}
                                                                                defaultValue={field.value}
                                                                                onChange={(e) => {
                                                                                    field.onChange(
                                                                                        e?.target?.value
                                                                                            ? [e?.target?.value]
                                                                                            : null
                                                                                    );
                                                                                }}
                                                                            />
                                                                        );
                                                                }
                                                            }}
                                                        />

                                                        <ErrorMessage
                                                            errors={errors}
                                                            name={`attributes[${index}].values[]`}
                                                            render={({message}) => (
                                                                <p className={styles.alert}>{message}</p>
                                                            )}
                                                        />
                                                    </FormItem>
                                                </Col>
                                            </>
                                        );
                                    })}
                                <Col span={24}>
                                    <FormItem label={t("file")}>
                                        <Controller
                                            name="pdfId"
                                            rules={{
                                                required: {
                                                    value: false,
                                                    message: `${t("field_is_required_message")}`,
                                                },
                                            }}
                                            render={({field: {ref, ...field}}) => {
                                                return (
                                                    <FileUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        onChange={(response) => {
                                                            field.onChange(response?.id);
                                                        }}
                                                        fileFor={MediumFor.PRODUCT_PDF}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>

                                {details?.pdf && (
                                    <a target="_blank" href={details?.pdf?.url}>
                                        download pdf
                                    </a>
                                )}
                                <Col span={24}>
                                    <FormItem label={t("image")}>
                                        <Controller
                                            name="imageId"
                                            rules={{
                                                required: {value: true, message: `${t("field_is_required_message")}`},
                                            }}
                                            render={({field: {ref, ...field}}) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        imageFor={MediumFor.UserProfile}
                                                        preserveOriginal={preserveOriginal}
                                                        onChange={(response) => {
                                                            if (response) {
                                                                setNewImage(response.url);
                                                                setNewImageFile(response.file);
                                                                setOriginalImageFile(response.originalFile);
                                                                setValue("without_background", false);
                                                            } else {
                                                                setNewImage(null);
                                                                setNewImageFile(undefined);
                                                                setOriginalImageFile(undefined);
                                                                setValue("without_background", details?.image?.without_background ?? false);
                                                            }
                                                            setPreserveOriginal(false);
                                                            field.onChange(response ? response.id : currentImageHidden ? undefined : details?.image?.id);
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <div style={{
                                    marginTop: 16,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    gap: 32,
                                    width: "100%",
                                    marginInline: 16
                                }}>
                                    {currentImageVisible && (
                                        <div style={{textAlign: "center", flex: "1 1 300px"}}>
                                            <h4>{t("old_image")}</h4>
                                            <div style={{position: "relative"}}>
                                                {!newImage && (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        shape="circle"
                                                        icon={<DeleteOutlined />}
                                                        aria-label={t("delete")}
                                                        title={t("delete")}
                                                        onClick={handleDeleteCurrentImage}
                                                        style={imageDeleteButtonStyle}
                                                    />
                                                )}
                                                <Image
                                                    src={details.image.url}
                                                    preview={true}
                                                    style={{width: "100%", maxHeight: 300, objectFit: "contain"}}
                                                    alt="Old Product"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {newImage && (
                                        <div style={{textAlign: "center", flex: "1 1 300px"}}>
                                            <h4>{t("new_image")}</h4>
                                            <div style={{position: "relative"}}>
                                                <Button
                                                    type="text"
                                                    danger
                                                    shape="circle"
                                                    icon={<DeleteOutlined />}
                                                    aria-label={t("delete")}
                                                    title={t("delete")}
                                                    onClick={handleRemoveNewImage}
                                                    style={imageDeleteButtonStyle}
                                                />
                                                <Image
                                                    src={newImage}
                                                    preview={true}
                                                    style={{width: "100%", maxHeight: 300, objectFit: "contain"}}
                                                    alt="New Preview"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {imageChanged && (
                                    <Col span={24}><Button
                                        type="primary"
                                        onClick={handleUploadNewImage}
                                        style={{marginTop: 16, display: "block"}}
                                        loading={imageLoading}
                                    >
                                        {t("upload_new_image")}
                                    </Button>
                                    </Col>
                                )}
                                {imageChanged && newImage && (
                                <Col span={24}>
                                    <FormItem label={t("image_processing_type")}>
                                        <Radio.Group
                                            value={imageProcessingMode}
                                            optionType="button"
                                            buttonStyle="solid"
                                            onChange={(e) => {
                                                const mode = e.target.value;
                                                setValue("without_background", mode === ImageProcessingMode.WithoutBackground);
                                                setPreserveOriginal(mode === ImageProcessingMode.Original);
                                            }}
                                        >
                                            <Radio.Button value={ImageProcessingMode.Avif}>
                                                {t("image_processing_avif")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.Original}>
                                                {t("preserve_original")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.WithoutBackground}>
                                                {t("without_background")}
                                            </Radio.Button>
                                        </Radio.Group>
                                    </FormItem>
                                </Col>
                                )}

                            </Row>
                        </div>
                    )}
                </div>

                <div>
                    <Divider className={styles.divider}/>
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const ProductForm = () => {
    return (
        <ProductContextProvider>
            <Form/>
        </ProductContextProvider>
    );
};
export default ProductForm;
